import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Anchor,
  Box,
  Breadcrumbs,
  Button,
  Card,
  Group,
  Loader,
  NumberInput,
  PasswordInput,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle, IconApps, IconLock } from "@tabler/icons-react";
import { apiFetch, apiPost, friendlyError, type ApiError } from "../../api/client.js";
import { useAuth } from "../../auth/AuthContext.js";
import { isApiError } from "./BackupsCard.js";
import { appActionError, type AppVariable, type CatalogAppDetail } from "./appUtils.js";

/** Matches the backend's APPS_DOMAIN (server-derived subdomains live here). */
const APPS_DOMAIN = "apps.noctools.nz";

interface ZodIssueLike {
  path?: (string | number)[];
  message?: string;
}

function mapIssuesToFields(
  err: ApiError,
  variables: AppVariable[]
): { errors: Record<string, string>; generic: string | null } {
  const empty = { errors: {}, generic: null as string | null };
  if (err.status !== 400 || err.error !== "invalid_request") return empty;
  const details = err.details as { issues?: ZodIssueLike[] } | null | undefined;
  const issues = Array.isArray(details?.issues) ? details!.issues : null;
  if (!issues) return empty;
  const errors: Record<string, string> = {};
  let generic: string | null = null;
  for (const issue of issues) {
    const parts = Array.isArray(issue?.path) ? issue.path.map(String) : [];
    const last = parts.length > 0 ? parts[parts.length - 1]! : "";
    if (last && variables.some((v) => v.key === last)) {
      errors[last] = issue?.message ?? "This value isn't valid.";
    } else {
      generic = generic ?? issue?.message ?? "Some values aren't valid.";
    }
  }
  return { errors, generic };
}

export function AppDeploy() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const auth = useAuth();
  const [app, setApp] = useState<CatalogAppDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [values, setValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [iconFailed, setIconFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!slug) return;
      try {
        const data = await apiFetch<CatalogAppDetail>(
          `/api/apps/catalog/${encodeURIComponent(slug)}`
        );
        if (cancelled) return;
        setIconFailed(false);
        const initial: Record<string, string> = {};
        for (const v of data.variables) {
          initial[v.key] = v.default ?? "";
        }
        setValues(initial);
        setApp(data);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        const apiErr = err as ApiError;
        if (apiErr.error === "unknown_app") {
          setError("This app doesn't exist or is no longer offered.");
        } else {
          setError(friendlyError(err, "Could not load this app."));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading && !app) {
    return (
      <Stack gap="xs" py="xl" align="center">
        <Loader />
        <Text c="dimmed" size="sm">
          Loading…
        </Text>
      </Stack>
    );
  }
  if (error && !app) {
    return (
      <Stack p="md" gap="md">
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
        <div>
          <Button variant="default" size="xs" w={140} onClick={() => navigate("/dashboard/apps")}>
            Back to list
          </Button>
        </div>
      </Stack>
    );
  }
  if (!app) return null;

  const usernameSegment = auth.session?.username
    ? auth.session.username.toLowerCase().replace(/_/g, "-")
    : null;
  const previewUrl =
    usernameSegment !== null
      ? `https://${app.slug}-${usernameSegment}.${APPS_DOMAIN}`
      : `https://${app.slug}-<your-name>.${APPS_DOMAIN}`;

  function setValue(key: string, value: string) {
    setValues((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function validateClient(): Record<string, string> {
    const errs: Record<string, string> = {};
    for (const v of app!.variables) {
      if (v.generate) continue;
      const val = values[v.key] ?? "";
      if (v.required && val.trim() === "") {
        errs[v.key] = "This field is required.";
        continue;
      }
      if (val === "" || !v.regex) continue;
      try {
        if (!new RegExp(v.regex).test(val)) {
          errs[v.key] = v.help ?? "This value doesn't match the required format.";
        }
      } catch {
        // Invalid regex on the template — skip client-side validation for it.
      }
    }
    return errs;
  }

  async function deploy() {
    if (!app || submitting) return;
    setFormError(null);
    const clientErrs = validateClient();
    if (Object.keys(clientErrs).length > 0) {
      setFieldErrors(clientErrs);
      setFormError("Please fix the highlighted fields.");
      return;
    }
    const variables: Record<string, string> = {};
    for (const v of app.variables) {
      const raw = values[v.key];
      if (raw !== undefined && raw !== "") variables[v.key] = raw;
    }
    const trimmedName = name.trim();
    setSubmitting(true);
    try {
      const res = await apiPost<{ id: number; status: string; subdomain: string; url: string }>(
        "/api/apps/deploy",
        { slug: app.slug, ...(trimmedName ? { name: trimmedName } : {}), variables }
      );
      notifications.show({
        color: "green",
        message: `${app.name} is being set up — this usually takes a minute.`,
      });
      navigate(`/dashboard/apps/instances/${res.id}`);
    } catch (err) {
      if (!isApiError(err)) {
        notifications.show({ color: "red", message: "Couldn't reach the server." });
        return;
      }
      const mapped = mapIssuesToFields(err, app.variables);
      if (Object.keys(mapped.errors).length > 0) {
        setFieldErrors(mapped.errors);
        setFormError(mapped.generic ?? "Please fix the highlighted fields.");
        return;
      }
      if (mapped.generic) {
        setFormError(mapped.generic);
        return;
      }
      const copy = appActionError(err, "Couldn't deploy this app. Try again in a moment.");
      notifications.show({ color: "red", title: copy.title, message: copy.message });
      if (err.error === "unknown_app") {
        setError(copy.message);
        setApp(null);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Stack gap="md">
      <Breadcrumbs>
        <Anchor component="button" type="button" size="sm" onClick={() => navigate("/dashboard/apps")}>
          Apps
        </Anchor>
        <Text size="sm" fw={600}>
          Deploy {app.name}
        </Text>
      </Breadcrumbs>

      <Card padding="lg">
        <Group gap="sm" wrap="nowrap" align="flex-start">
          {app.icon && !iconFailed ? (
            <img
              src={app.icon}
              alt=""
              width={34}
              height={34}
              onError={() => setIconFailed(true)}
            />
          ) : (
            <ThemeIcon size="lg" variant="light" color="brand" radius="md" style={{ flex: "0 0 auto" }}>
              <IconApps size={18} stroke={1.75} />
            </ThemeIcon>
          )}
          <Stack gap={4} style={{ flex: 1 }}>
            <Title order={2}>{app.name}</Title>
            <Text size="sm" c="dimmed">
              {app.description}
            </Text>
            <Text size="xs" c="dimmed">
              Template version {app.version}
            </Text>
          </Stack>
        </Group>
      </Card>

      <Card padding="lg">
        <Stack gap="sm">
          <Group gap="xs">
            <IconApps size={18} stroke={1.75} />
            <Title order={3}>Deploy {app.name}</Title>
          </Group>
          <Text size="sm" c="dimmed">
            Your app gets its own web address and runs isolated from your other services.
          </Text>

          <TextInput
            label="Name"
            placeholder={`Defaults to "${app.name}"`}
            description="Shown in your apps list."
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            maxLength={63}
          />

          <Box
            style={{
              border: "1px dashed var(--mantine-color-default-border)",
              borderRadius: 8,
              padding: "10px 14px",
            }}
          >
            <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={2}>
              Web address
            </Text>
            <Text size="sm" ff="monospace" style={{ wordBreak: "break-all" }}>
              {previewUrl}
            </Text>
            {usernameSegment === null && (
              <Text size="xs" c="dimmed" mt={4}>
                The exact address appears here after the app is deployed.
              </Text>
            )}
          </Box>

          {app.variables.length > 0 && (
            <Stack gap="md" mt="xs">
              <Text size="sm" fw={600}>
                Settings
              </Text>
              {app.variables.map((v) => (
                <AppVariableField
                  key={v.key}
                  variable={v}
                  value={values[v.key] ?? ""}
                  error={fieldErrors[v.key]}
                  onChange={(val) => setValue(v.key, val)}
                />
              ))}
            </Stack>
          )}

          {formError && (
            <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
              {formError}
            </Alert>
          )}

          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={() => navigate("/dashboard/apps")} disabled={submitting}>
              Cancel
            </Button>
            <Button loading={submitting} onClick={() => void deploy()}>
              Deploy {app.name}
            </Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}

function AppVariableField({
  variable,
  value,
  error,
  onChange,
}: {
  variable: AppVariable;
  value: string;
  error?: string;
  onChange: (value: string) => void;
}) {
  if (variable.generate) {
    return (
      <Group gap="xs" wrap="nowrap" align="flex-start">
        <ThemeIcon
          size="sm"
          variant="light"
          color="gray"
          radius="md"
          style={{ flex: "0 0 auto", marginTop: 22 }}
        >
          <IconLock size={14} stroke={1.75} />
        </ThemeIcon>
        <Stack gap={1}>
          <Text size="sm" fw={500}>
            {variable.label}
          </Text>
          <Text size="xs" c="dimmed">
            Auto-generated during deployment
          </Text>
          {variable.help && (
            <Text size="xs" c="dimmed">
              {variable.help}
            </Text>
          )}
        </Stack>
      </Group>
    );
  }
  const common = {
    label: variable.label,
    description: variable.help,
    required: variable.required,
    error,
  };
  if (variable.type === "password") {
    return (
      <PasswordInput
        {...common}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
      />
    );
  }
  if (variable.type === "number") {
    return (
      <NumberInput
        {...common}
        value={value === "" ? undefined : value}
        onChange={(val) => onChange(val === undefined || val === "" ? "" : String(val))}
        hideControls
      />
    );
  }
  if (variable.type === "enum") {
    return (
      <Select
        {...common}
        data={(variable.enum_values ?? []).map((v) => ({ value: v, label: v }))}
        value={value === "" ? null : value}
        onChange={(val) => onChange(val ?? "")}
        clearable={!variable.required}
      />
    );
  }
  return (
    <TextInput {...common} value={value} onChange={(e) => onChange(e.currentTarget.value)} />
  );
}
